/**
 * DOM-only autofill hardening for the new-supported wizard (react-hook-form paths unchanged).
 * Chrome often ignores autocomplete="off"; neutral id/name (see FORBIDDEN_* below) is the main mitigation.
 * We still set autocomplete="off" on these fields per security review (SPR-1241 S1).
 */
export const SUPPORTED_PERSON_AUTOCOMPLETE = 'off';

/** Substrings that must not appear in autocomplete values on supported-person fields. */
export const FORBIDDEN_AUTOCOMPLETE_SUBSTRINGS = [
  'section-',
  'email',
  'tel',
  'address',
  'postal',
  'street',
  'city',
  'name',
  'line1',
  'line2',
  'zip',
  'country',
  'organization',
  'username',
  'password',
] as const;

/** Must not appear in id or name attributes (Chrome heuristics). */
export const FORBIDDEN_DOM_ID_NAME_SUBSTRINGS = [
  'city',
  'street',
  'address',
  'house',
  'zip',
  'postal',
  'mobile',
  'phone',
  'tel',
  'email',
  'mail',
  'first',
  'last',
  'name',
] as const;

export const SUPPORTED_PERSON_FIELD_DOM = {
  firstName: { id: 'sp-f1', name: 'sp-f1' },
  lastName: { id: 'sp-f2', name: 'sp-f2' },
  city: { id: 'sp-f3', name: 'sp-f3' },
  streetName: { id: 'sp-f4', name: 'sp-f4' },
  streetNumber: { id: 'sp-f5', name: 'sp-f5' },
  zipCode: { id: 'sp-f6', name: 'sp-f6' },
  phone: { id: 'sp-f7', name: 'sp-f7' },
  email: { id: 'sp-f8', name: 'sp-f8' },
} as const;

/** ID check step (CheckExistence) — autocomplete off per security review. */
export const SUPPORTED_ID_CHECK_DOM = { id: 'sp-f9', name: 'sp-f9' } as const;

/** Spouse step (FamilyInfo) — DOM only; section may be commented out in UI. */
export const SPOUSE_FIELD_DOM = {
  israeliId: { id: 'sp-s1', name: 'sp-s1' },
  firstName: { id: 'sp-s2', name: 'sp-s2' },
} as const;

/** Children step — neutral ids per child index (slots 1–3: name, id, birth date). */
export function childFieldDom(childIndex: number, slot: 1 | 2 | 3) {
  const suffix = `sp-c${childIndex}-f${slot}`;
  return { id: suffix, name: suffix };
}

export function assertSupportedPersonAutocomplete(value: string | null | undefined) {
  if (!value) throw new Error('missing autocomplete');
  const lower = value.toLowerCase();
  for (const part of FORBIDDEN_AUTOCOMPLETE_SUBSTRINGS) {
    if (lower.includes(part)) {
      throw new Error(`autocomplete contains forbidden token: ${part}`);
    }
  }
  if (value !== SUPPORTED_PERSON_AUTOCOMPLETE) {
    throw new Error(`expected ${SUPPORTED_PERSON_AUTOCOMPLETE}, got ${value}`);
  }
}

export function assertNeutralDomIdOrName(value: string | null | undefined) {
  if (!value) throw new Error('missing id/name');
  const lower = value.toLowerCase();
  for (const part of FORBIDDEN_DOM_ID_NAME_SUBSTRINGS) {
    if (lower.includes(part)) {
      throw new Error(`id/name contains forbidden substring: ${part}`);
    }
  }
}
